import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { getPhotoSrc, SILHOUETTE_AVATAR } from '../lib/avatar';
import api from '../lib/api';

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const PLACEHOLDER = SILHOUETTE_AVATAR;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click / Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  // WebSocket listener for real-time notifications
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('yublink_token');
    if (!token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/chat?token=${token}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            setNotifications(prev => [data.notification, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        } catch {}
      };
      ws.onclose = () => {};
    } catch {}

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  if (!user) return null;

  const getTimeAgo = (isoStr) => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
        className="relative p-2 rounded-xl text-[#9EABCE] hover:text-white hover:bg-white/5 transition-all"
      >
        <Bell weight={unreadCount > 0 ? 'fill' : 'light'} size={20} className={unreadCount > 0 ? 'text-[#D4AF37]' : ''} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D4AF37] text-[#060A1A] text-[10px] font-bold rounded-full flex items-center justify-center animate-heartbeat" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-2xl overflow-hidden z-50 shadow-2xl" style={{ backgroundColor: '#111111' }} data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="text-white font-semibold text-sm font-['Outfit']">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                data-testid="mark-all-read-btn"
                className="text-xs text-[#D4AF37] hover:text-[#F0C84A] transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center" style={{ backgroundColor: '#111111' }}>
                <Bell size={32} weight="light" className="text-[#555] mx-auto mb-2" />
                <p className="text-[#888] text-xs">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 20).map((notif) => {
                const photo = getPhotoSrc(notif.from_user?.photos?.[0]) || PLACEHOLDER;
                return (
                  <Link
                    key={notif.id}
                    to={notif.type === 'message' ? '/chat' : `/profile/${notif.from_user_id}`}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 p-3 hover:bg-[#1a1a1a] transition-all border-b border-[#222] ${
                      !notif.read ? 'bg-[#1a1a0f]' : ''
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs truncate ${!notif.read ? 'text-white font-semibold' : 'text-[#ccc]'}`}>
                        {notif.title}
                      </p>
                      <p className="text-[11px] text-[#999] truncate mt-0.5">{notif.body}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] text-[#777]">{getTimeAgo(notif.created_at)}</span>
                      {!notif.read && <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
